import { CSSProperties, FC, useState } from 'react';
import Image from 'next/image';

import { icon_3d_cube, icon_maximize } from '@gn-audio/icons';
import { Button, isColorBright } from '@gn-audio/jss-components';
import { JabraIcon } from '@gn-audio/jss-components';
import { JabraDialog } from '@gn-audio/jss-components/components/JabraDialog';

import { FamilyGroup } from 'api/productCatalogueApi';
import { JabraSlide, JabraSlider } from 'common/components/JabraSlider/JabraSlider';
import PlaceholderSlide from 'common/components/JabraSlider/PlaceholderSlide';
import { Product3DView } from 'common/components/Product3dView';
import { classNames } from 'common/utils/classNames';
import { FullScreenImageSlider } from 'composables/Product/Buy/ImageSlider/FullScreenImageSlider';
import { ImageThumbnail } from 'composables/Product/Buy/ImageSlider/ImageThumbnail';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';
import { getBackgroundColorHex } from 'utils/productUtils';

import { getGroupAttributesObject } from '../customizerHelpers';

import styles from './ImageSlider.module.scss';

export interface ProductImageSliderProps {
  group?: FamilyGroup;
}

export interface ImageThumbnailProps {
  src: string;
  alt: string;
  selected: boolean;
}

export const ImageSlider: FC<ProductImageSliderProps> = ({ group }) => {
  const prodSpecs = getGroupAttributesObject(group);

  const [uuid] = prodSpecs.Meta_3dRenderingUuid ?? [];

  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const bgColorHex = getBackgroundColorHex(group);
  const isBgBright = !!bgColorHex ? isColorBright(bgColorHex) : true;

  const { t } = useFallbackTranslation();
  const handleOpenFullScreen = () => {
    setIsFullScreenOpen(true);
  };

  const slides =
    group?.images.map((image, index) => {
      return (
        <JabraSlide
          key={image.url}
          renderThumbnail={(selected) => (
            <ImageThumbnail selected={selected} alt={group.productName} src={image.url} />
          )}
        >
          <Image
            className="h-[12.5rem] object-contain max-md:my-8 max-sm:my-16 sm:h-[18.75rem] xl:h-[31.25rem]"
            src={`https:${image.url}`}
            alt={group.name}
            width={500}
            priority={index === 0}
            height={500}
            loading={index === 0 ? 'eager' : 'lazy'}
          />
        </JabraSlide>
      );
    }) || [];

  if (uuid && group) {
    slides.push(
      <JabraSlide
        key="3d"
        renderThumbnail={(selected) => {
          return (
            <div
              className={classNames(
                'bg-black bg-opacity-8 outline-2 outline-focus group-focus-visible/slider-thumbnail:outline dark:bg-opacity-24',
                styles.smallThumb,
                selected && styles.selected
              )}
            >
              <JabraIcon className="h-[2.5rem]" Icon={icon_3d_cube} aria-label={t('system.3d')} />
            </div>
          );
        }}
      >
        <Product3DView productUuid={uuid} productName={group.productName} />
      </JabraSlide>
    );
  }

  const bgStyle = { '--bgColor': bgColorHex } as CSSProperties;
  return (
    <div style={bgStyle} className="md:mb-[1.5rem] xl:mb-0">
      <div
        className={classNames(
          `flex flex-col items-center gap-4 py-6 transition-colors md:overflow-hidden md:rounded-r-full md:bg-[var(--bgColor)] md:bgl-[var(--bgColor)] xl:py-[9rem] xl:pl-[1.5rem] xl:pr-[6rem]`,
          !isBgBright ? 'dark' : 'light'
        )}
      >
        <div className="w-full">
          <JabraSlider
            mainClassName="max-md:rounded-r-full max-md:bg-[var(--bgColor)]  max-md:overflow-hidden"
            footerClassName="mt-8 max-md:[&>ul]:justify-start max-w-[45rem]  max-xl:container py-0"
            slideClassName="basis-full"
            showSlideArrows={false}
            showThumbnailArrows={true}
          >
            {!group ? [<PlaceholderSlide key="1" />, <PlaceholderSlide key="2" />] : slides}
          </JabraSlider>
        </div>

        <footer className="hidden md:block">
          <Button
            onClick={handleOpenFullScreen}
            variant="tertiary"
            size="m"
            iconPlacement="right"
            icon={<JabraIcon Icon={icon_maximize} />}
          >
            <span>{t('system.CustomizerFullScreen')}</span>
          </Button>
        </footer>
        {group && (
          <JabraDialog
            fullScreen
            open={isFullScreenOpen}
            onClose={() => setIsFullScreenOpen(false)}
          >
            <FullScreenImageSlider group={group} />
          </JabraDialog>
        )}
      </div>
    </div>
  );
};
